import {DailyViewTotalizersDisplay, PLACEHOLDER} from "./DailyViewTotalizers";

export type DailyOverviewTotalizersPayload = {
    normalizedPowerStations: number;
    totalPowerStations: number;
    generatedEnergy: number | null;
    expectedEnergy: number | null;
    criticalActiveAlarms: number;
    equipmentNoCommunication: number;
    averageAvailability: number | null;
};

const formatCount = (value: number | null | undefined): string => {
    if (value === null || value === undefined) {
        return PLACEHOLDER;
    }
    return String(value);
};

export const formatNormalizedRatio = (normalized: number, total: number): string =>
    `${normalized}/${total}`;

export const formatEnergyMwh = (value: number | null | undefined): string => {
    if (value === null || value === undefined) {
        return PLACEHOLDER;
    }
    return value.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

export const formatAvailabilityPercent = (value: number | null | undefined): string => {
    if (value === null || value === undefined) {
        return PLACEHOLDER;
    }
    return value.toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
    });
};

export const formatAlarmCount = (value: number | null | undefined): string => formatCount(value);

export const toDailyViewTotalizersDisplay = (
    payload: DailyOverviewTotalizersPayload | undefined,
    preserveExpired: boolean
): DailyViewTotalizersDisplay | undefined => {
    if (!payload || preserveExpired) {
        return undefined;
    }

    const generated = formatEnergyMwh(payload.generatedEnergy);
    const expected = formatEnergyMwh(payload.expectedEnergy);

    return {
        normalizedRatio: formatNormalizedRatio(
            payload.normalizedPowerStations,
            payload.totalPowerStations
        ),
        normalizedPowerStations: formatCount(payload.normalizedPowerStations),
        totalPowerStations: formatCount(payload.totalPowerStations),
        generatedEnergy: generated,
        expectedEnergy: expected,
        criticalAlarms: formatAlarmCount(payload.criticalActiveAlarms),
        equipmentNoCommunication: formatAlarmCount(payload.equipmentNoCommunication),
        availabilityPercent: formatAvailabilityPercent(payload.averageAvailability),
    };
};

/** Conjunto vazio (menu carregado sem usinas): 0/0 e placeholders nos demais cards. */
export const toEmptySelectionDailyViewTotalizersDisplay = (): DailyViewTotalizersDisplay => ({
    normalizedRatio: formatNormalizedRatio(0, 0),
    normalizedPowerStations: formatCount(0),
    totalPowerStations: formatCount(0),
});

export const isValidDailyOverviewTotalizersPayload = (
    value: unknown
): value is DailyOverviewTotalizersPayload => {
    if (!value || typeof value !== "object") {
        return false;
    }
    const data = value as Record<string, unknown>;
    return typeof data.normalizedPowerStations === "number"
        && typeof data.totalPowerStations === "number"
        && (data.generatedEnergy === null || typeof data.generatedEnergy === "number")
        && (data.expectedEnergy === null || typeof data.expectedEnergy === "number")
        && typeof data.criticalActiveAlarms === "number"
        && typeof data.equipmentNoCommunication === "number"
        && (data.averageAvailability === null || typeof data.averageAvailability === "number");
};
